import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { PageTransition } from '@/components/PageTransition';
import { useSpaceData } from '@/hooks/useSpaceData';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { AsyncCombobox } from '@/components/ui/async-combobox';
import { supabase } from '@/lib/supabase';
import {
  Check,
  CheckCircle2,
  Circle,
  ArrowLeft,
  Clock,
  User,
  Users,
  Calendar,
  FileText,
  Image as ImageIcon,
  Video,
  File,
  Mail,
  Link2,
  ListIcon,
  Table as TableIcon,
  ChevronRight,
  Download,
  Upload,
  X,
  Loader2,
  Plus,
  Trash2,
  ExternalLink,
  XCircle,
  Ban,
  UserPlus,
  PenLine,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import WorkflowRunComments from '@/components/workflows/WorkflowRunComments';
import { Zap, FlagTriangleRight, AlertCircle } from 'lucide-react';
import { triggerFlow } from '@/lib/aiFlowsService';
import FlowExecutionScreen from './FlowExecutionScreen';

/**
 * Format a form field value for display in variable replacement
 * Handles different field types (strings, arrays, objects, etc.)
 */
function formatFieldValueForDisplay(value, fieldType) {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  // Handle arrays (Multi Choice, Table)
  if (Array.isArray(value)) {
    if (fieldType === 'Table') {
      // For tables, show row count
      return `${value.length} row${value.length !== 1 ? 's' : ''}`;
    }
    // For multi-choice, join with commas
    return value.join(', ');
  }

  // Handle objects
  if (typeof value === 'object') {
    // User field
    if (value.first_name || value.last_name) {
      return `${value.first_name || ''} ${value.last_name || ''}`.trim();
    }
    // File upload
    if (value.name && value.url) {
      return value.name;
    }
    // Signature (base64 data URL) - just indicate it exists
    if (typeof value === 'string' && value.startsWith('data:image')) {
      return '[Signature]';
    }
    // Generic object - stringify
    return JSON.stringify(value);
  }

  // Handle signature data URL at top level
  if (typeof value === 'string' && value.startsWith('data:image')) {
    return '[Signature]';
  }

  // Handle dates - format nicely
  if (fieldType === 'Date' && value) {
    try {
      return new Date(value).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return value;
    }
  }

  return String(value);
}

/**
 * Flatten a flow result object into dot-notation keys for variable replacement.
 * e.g. { files: ["url"] } → { "flow.files": "url", "flow.files.0": "url" }
 * Also produces "flow.result.*" aliases so both {{ flow.files.0 }} and
 * {{ flow.result.files.0 }} work (the latter matches raw API response paths).
 */
function flattenFlowResult(result) {
  const flattened = {};
  if (!result || typeof result !== 'object') return flattened;

  function flatten(obj, prefix) {
    if (obj === null || obj === undefined) return;
    if (Array.isArray(obj)) {
      obj.forEach((item, index) => flatten(item, `${prefix}.${index}`));
      if (obj.length > 0) flattened[prefix] = String(obj[0]);
      return;
    }
    if (typeof obj === 'object') {
      Object.entries(obj).forEach(([key, value]) => flatten(value, `${prefix}.${key}`));
      return;
    }
    flattened[prefix] = String(obj);
  }

  flatten(result, 'flow');

  // Add "flow.result.*" aliases for each "flow.*" key
  const aliases = {};
  for (const [key, value] of Object.entries(flattened)) {
    if (key.startsWith('flow.')) {
      aliases[`flow.result.${key.slice(5)}`] = value;
    }
  }
  Object.assign(flattened, aliases);

  return flattened;
}

/**
 * Flatten webhook data into dot-notation keys for variable replacement.
 * e.g. { status: "approved", files: ["url"] } → { "webhook.status": "approved", "webhook.files.0": "url" }
 */
function flattenWebhookData(data) {
  const flattened = {};
  if (!data || typeof data !== 'object') return flattened;

  function flatten(obj, prefix) {
    if (obj === null || obj === undefined) return;
    if (Array.isArray(obj)) {
      obj.forEach((item, index) => flatten(item, `${prefix}.${index}`));
      if (obj.length > 0) flattened[prefix] = String(obj[0]);
      return;
    }
    if (typeof obj === 'object') {
      Object.entries(obj).forEach(([key, value]) => flatten(value, `${prefix}.${key}`));
      return;
    }
    flattened[prefix] = String(obj);
  }

  flatten(data, 'webhook');
  return flattened;
}

/**
 * Convert a field name to a URL-safe slug for use as payload keys.
 * e.g., "First Name" → "first_name", "Email Address" → "email_address"
 */
function toSlug(name) {
  if (!name) return '';
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

/**
 * Transform form_data from raw field IDs to friendly slugified names.
 * This makes flow payloads reusable across different processes.
 * e.g., { "input-28475abc": "John" } → { "first_name": "John" }
 */
function buildFriendlyFormData(formData, workflowTasks) {
  if (!formData || !workflowTasks) return formData || {};

  // Build ID → slug mapping from all tasks' form fields
  const idToSlug = {};
  const slugCounts = {};

  workflowTasks.forEach(task => {
    (task.content || [])
      .filter(item => item.category === 'form')
      .forEach(item => {
        const name = item.content?.fieldName || item.label || '';
        let slug = toSlug(name);
        if (!slug) slug = item.id;

        // Handle duplicate slugs by appending a number
        if (slugCounts[slug]) {
          slugCounts[slug]++;
          idToSlug[item.id] = `${slug}_${slugCounts[slug]}`;
        } else {
          slugCounts[slug] = 1;
          idToSlug[item.id] = slug;
        }
      });
  });

  // Transform keys from raw IDs to friendly slugs
  const friendly = {};
  for (const [key, value] of Object.entries(formData)) {
    friendly[idToSlug[key] || key] = value;
  }
  return friendly;
}

/**
 * Replace variable placeholders in HTML content with actual form data values.
 * Supports both raw field IDs ({{ input-xxx }}) and friendly slugs ({{ first_name }}).
 * The slug mapping is built from allFields so existing workflows remain compatible.
 */
function replaceVariablesInContent(htmlContent, formData, allFields) {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return htmlContent || '';
  }

  // Build a slug → rawID mapping from allFields so {{ first_name }} resolves to the value
  // stored under the raw ID in formData. This supports both old and new variable formats.
  let slugToId = null;
  const getSlugMap = () => {
    if (slugToId) return slugToId;
    slugToId = {};
    if (allFields) {
      const slugCounts = {};
      for (const [id, item] of Object.entries(allFields)) {
        const name = item.content?.fieldName || item.label || '';
        let slug = toSlug(name);
        if (!slug) continue;
        if (slugCounts[slug]) {
          slugCounts[slug]++;
          slug = `${slug}_${slugCounts[slug]}`;
        } else {
          slugCounts[slug] = 1;
        }
        slugToId[slug] = id;
      }
    }
    return slugToId;
  };

  // Replace {{ variableKey }} patterns
  return htmlContent.replace(/\{\{\s*([^}]+)\s*\}\}/g, (match, key) => {
    const trimmedKey = key.trim();

    // Normalize bracket notation to dot notation: flow.files[0] → flow.files.0
    const normalizedKey = trimmedKey.replace(/\[(\d+)\]/g, '.$1');

    // Try direct lookup by raw ID or normalized key
    let lookupKey = formData?.[trimmedKey] !== undefined ? trimmedKey
      : formData?.[normalizedKey] !== undefined ? normalizedKey
      : null;

    // If not found, try resolving as a friendly slug → raw ID
    if (lookupKey === null) {
      const map = getSlugMap();
      const rawId = map[trimmedKey];
      if (rawId && formData?.[rawId] !== undefined) {
        lookupKey = rawId;
      }
    }

    if (lookupKey !== null) {
      const fieldInfo = allFields?.[lookupKey];
      const fieldType = fieldInfo?.label || '';
      const value = formatFieldValueForDisplay(formData[lookupKey], fieldType);
      return value || `<span class="text-muted-foreground italic">[Not provided]</span>`;
    }

    // Check for system variables (current_user, current_date, etc.)
    if (trimmedKey.startsWith('current_user.')) {
      // These would need to be passed in - for now return placeholder
      return `<span class="text-muted-foreground italic">[${trimmedKey}]</span>`;
    }

    if (trimmedKey.startsWith('current_date.') || trimmedKey === 'current_time') {
      const now = new Date();
      switch (trimmedKey) {
        case 'current_date.full':
          return now.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
        case 'current_date.short':
          return now.toLocaleDateString('en-GB');
        case 'current_date.iso':
          return now.toISOString().split('T')[0];
        case 'current_time':
          return now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        default:
          return `[${trimmedKey}]`;
      }
    }

    // Unknown variable - show as placeholder
    return `<span class="text-muted-foreground italic">[${trimmedKey}]</span>`;
  });
}

// Signature Capture Component
function SignatureCapture({ value, onChange, fieldName, renderLabel }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const getCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e, canvas);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e, canvas);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      const dataUrl = canvas.toDataURL('image/png');
      onChange(dataUrl);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    onChange(null);
  };

  // Initialize canvas when mounted or when there's existing signature data
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000';

    // Fill white background
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // If there's existing signature data, draw it
    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
      };
      img.src = value;
    }
  }, []);

  return (
    <div className="space-y-2">
      {renderLabel(fieldName)}
      <div className="border-2 rounded-lg overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          width={500}
          height={150}
          className="w-full h-[150px] cursor-crosshair touch-none"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <PenLine className="h-3 w-3" />
          <span>Draw your signature above</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={clearSignature}
          className="h-7 text-xs"
        >
          <RotateCcw className="h-3 w-3 mr-1" />
          Clear
        </Button>
      </div>
    </div>
  );
}

// Dynamic Dropdown Component - fetches options from API or AiTables
function DynamicDropdown({ item, value, onChange, renderLabel, fieldName, companyId }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const dataSource = item.content?.dataSource || {};
  const aiTablesConfig = item.content?.aiTablesConfig || {};
  const dataSourceType = item.content?.dataSourceType || 'static';

  useEffect(() => {
    // Static options - use stored options directly
    if (dataSourceType === 'static') {
      setOptions(item.content?.options || []);
      return;
    }

    // Dynamic API fetch
    if (dataSourceType === 'dynamic') {
      if (!dataSource.url) {
        setOptions([]);
        return;
      }

      const fetchOptions = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await fetch(dataSource.url);
          if (!response.ok) {
            throw new Error(`Failed to fetch options: ${response.status}`);
          }
          const data = await response.json();

          // Navigate to data path if specified
          let items = data;
          if (dataSource.dataPath) {
            const pathParts = dataSource.dataPath.split('.');
            for (const part of pathParts) {
              items = items?.[part];
            }
          }

          if (!Array.isArray(items)) {
            throw new Error('Data source did not return an array');
          }

          // Map to option format
          const valueField = dataSource.valueField || 'id';
          const labelField = dataSource.labelField || 'name';
          const mappedOptions = items.map((item, idx) => ({
            id: item[valueField] || idx,
            value: String(item[valueField] || idx),
            label: String(item[labelField] || item[valueField] || idx)
          }));

          setOptions(mappedOptions);
        } catch (err) {
          console.error('Error fetching dropdown options:', err);
          setError(err.message);
          setOptions([]);
        } finally {
          setLoading(false);
        }
      };

      fetchOptions();
      return;
    }

    // AiTables fetch
    if (dataSourceType === 'aitables') {
      if (!companyId || !aiTablesConfig.tableId) {
        setError('AiTables configuration incomplete');
        setOptions([]);
        return;
      }

      const fetchAiTablesOptions = async () => {
        setLoading(true);
        setError(null);
        try {
          const { getDropdownOptions } = await import('@/lib/aitablesService');
          const mappedOptions = await getDropdownOptions(
            companyId,
            aiTablesConfig.tableId,
            aiTablesConfig.valueColumn || 'id',
            aiTablesConfig.labelColumn || 'name'
          );
          setOptions(mappedOptions);
        } catch (err) {
          console.error('Error fetching AiTables dropdown options:', err);
          setError(err.message || 'Failed to fetch options from AiTables');
          setOptions([]);
        } finally {
          setLoading(false);
        }
      };

      fetchAiTablesOptions();
      return;
    }

    // Fallback for unknown types
    setOptions(item.content?.options || []);
  }, [dataSourceType, dataSource.url, dataSource.dataPath, dataSource.valueField, dataSource.labelField, item.content?.options, companyId, aiTablesConfig.tableId, aiTablesConfig.valueColumn, aiTablesConfig.labelColumn]);

  return (
    <div className="space-y-2">
      {renderLabel(fieldName)}
      {loading ? (
        <div className="h-10 flex items-center justify-center border rounded-md bg-muted/50">
          <span className="text-sm text-muted-foreground">Loading options...</span>
        </div>
      ) : error ? (
        <div className="p-2 border border-destructive/50 rounded-md bg-destructive/10">
          <p className="text-xs text-destructive">{error}</p>
        </div>
      ) : (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.id} value={option.value}>
                {option.label || option.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

export default function WorkflowRunPage() {
  const navigate = useNavigate();
  const { projectId, runId } = useParams();
  const { userProfile, company } = useAuth();
  // Pass company.id to validate the space belongs to the user's current company
  const { space, spaceTools, loading, accessDenied } = useSpaceData(projectId, userProfile?.id, company?.id);

  // Set page title
  usePageTitle('Workflow Run');

  // Redirect if user doesn't have access (e.g., switched companies)
  useEffect(() => {
    if (accessDenied) {
      toast.error('You no longer have access to this space');
      navigate('/dashboard', { replace: true });
    }
  }, [accessDenied, navigate]);

  const [workflowRun, setWorkflowRun] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [taskRuns, setTaskRuns] = useState([]);
  const [activeTaskRunId, setActiveTaskRunId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingFields, setUploadingFields] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [isCancelling, setIsCancelling] = useState(false);

  // AI Flow execution state
  const [isFlowExecuting, setIsFlowExecuting] = useState(false);
  const [flowExecutionMessage, setFlowExecutionMessage] = useState('Running automation...');
  const [flowOutputData, setFlowOutputData] = useState({});
  const [flowError, setFlowError] = useState(null);
  const [flowRetryCount, setFlowRetryCount] = useState(0);
  const flowExecutionRef = useRef(null); // Track which task run is currently executing a flow

  // Debounce refs for form changes
  const debounceTimerRef = useRef({});
  const pendingChangesRef = useRef({});
  // Track locally-inserted task run IDs to prevent duplicates from real-time subscription
  const locallyInsertedIdsRef = useRef(new Set());

  // Load workflow run data with all context in a single RPC call
  // This replaces 4 separate queries (run, workflow, task_runs, team_members)
  useEffect(() => {
    const loadWorkflowRunWithContext = async () => {
      if (!runId || !projectId) return;

      try {
        // Single RPC call fetches everything: run, workflow, task_runs, team_members
        const { data, error } = await supabase.rpc('get_workflow_run_with_context', {
          p_run_id: runId,
          p_project_id: projectId
        });

        if (error) throw error;

        if (!data) {
          toast.error('Workflow run not found');
          return;
        }

        // Set all state from the single response
        setWorkflowRun(data.workflow_run);
        setWorkflow(data.workflow);
        setTaskRuns(data.task_runs || []);
        setTeamMembers(data.team_members || []);

        // Load webhook trigger data from the run itself (payload from initial webhook POST)
        const webhookVars = {};
        if (data.workflow_run?.webhook_data && typeof data.workflow_run.webhook_data === 'object') {
          Object.assign(webhookVars, flattenWebhookData(data.workflow_run.webhook_data));
        }

        // Also load webhook data from any already-completed flow tasks
        const taskRunsData = data.task_runs || [];
        taskRunsData.forEach(tr => {
          if (tr.webhook_data && typeof tr.webhook_data === 'object') {
            Object.assign(webhookVars, flattenWebhookData(tr.webhook_data));
          }
        });
        if (Object.keys(webhookVars).length > 0) {
          setFlowOutputData(prev => ({ ...prev, ...webhookVars }));
        }

        // Set active task to first in-progress or pending task
        const activeTask = taskRunsData.find(t => t.status === 'in_progress') ||
                          taskRunsData.find(t => t.status === 'pending');
        if (activeTask) {
          setActiveTaskRunId(activeTask.id);
        }
      } catch (error) {
        console.error('Error loading workflow run:', error);
        toast.error('Failed to load workflow run');
      } finally {
        setIsLoading(false);
      }
    };

    loadWorkflowRunWithContext();
  }, [runId, projectId]);

  // Real-time subscription for workflow task runs
  useEffect(() => {
    if (!runId) return;

    // Use a flag to track if this effect instance is still active
    // This handles React Strict Mode double-mounting
    let isActive = true;
    let taskRunsChannel = null;
    let workflowRunChannel = null;

    // Unique ID for this effect instance to avoid channel name conflicts
    const instanceId = Date.now();

    const setupSubscriptions = async () => {
      console.log('Setting up real-time subscriptions for runId:', runId, 'instance:', instanceId);

      // Set auth token for Realtime (required for RLS policies to work)
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        await supabase.realtime.setAuth(session.access_token);
        console.log('Realtime auth token set for workflow subscriptions');
      } else {
        console.warn('No session found - realtime may not receive events due to RLS');
      }

      // Subscribe to changes on task runs for this workflow run
      taskRunsChannel = supabase
        .channel(`workflow-task-runs-${runId}-${instanceId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'checklists_workflow_task_runs',
            filter: `workflow_run_id=eq.${runId}`,
          },
          (payload) => {
            if (!isActive) return; // Ignore if effect was cleaned up
            console.log('Task run change received:', payload);

            if (payload.eventType === 'INSERT') {
              // Skip if this ID was locally inserted (prevents duplicates from race condition)
              if (locallyInsertedIdsRef.current.has(payload.new.id)) {
                locallyInsertedIdsRef.current.delete(payload.new.id);
                return;
              }
              setTaskRuns(prev => {
                if (prev.find(t => t.id === payload.new.id)) return prev;
                return [...prev, payload.new].sort((a, b) => a.task_order - b.task_order);
              });
            } else if (payload.eventType === 'UPDATE') {
              const pendingChanges = pendingChangesRef.current[payload.new.id];
              setTaskRuns(prev =>
                prev.map(t => {
                  if (t.id !== payload.new.id) return t;
                  if (pendingChanges) {
                    return {
                      ...payload.new,
                      form_data: { ...payload.new.form_data, ...pendingChanges },
                    };
                  }
                  return payload.new;
                })
              );
            } else if (payload.eventType === 'DELETE') {
              setTaskRuns(prev => prev.filter(t => t.id !== payload.old.id));
            }
          }
        )
        .subscribe((status, err) => {
          if (!isActive) return;
          console.log('Task runs subscription status:', status, err);
        });

      // Subscribe to changes on the workflow run itself
      workflowRunChannel = supabase
        .channel(`workflow-run-${runId}-${instanceId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'checklists_workflow_runs',
            filter: `id=eq.${runId}`,
          },
          (payload) => {
            if (!isActive) return;
            console.log('Workflow run change:', payload);
            setWorkflowRun(prev => ({
              ...prev,
              ...payload.new,
              workflows: prev?.workflows,
            }));
          }
        )
        .subscribe((status, err) => {
          if (!isActive) return;
          console.log('Workflow run subscription status:', status, err);
        });
    };

    setupSubscriptions();

    // Cleanup subscriptions on unmount
    return () => {
      console.log('Cleaning up real-time subscriptions for runId:', runId);
      isActive = false;
      if (taskRunsChannel) supabase.removeChannel(taskRunsChannel);
      if (workflowRunChannel) supabase.removeChannel(workflowRunChannel);
    };
  }, [runId]);

  /**
   * MEMOIZED: Map of workflow tasks by ID for O(1) lookups
   * Defined early so it can be used by other memoized values
   * Replaces O(n) .find() calls with O(1) Map.get()
   */
  const workflowTaskMap = useMemo(() => {
    const map = new Map();
    workflow?.tasks?.forEach(task => {
      map.set(String(task.id), task);
    });
    return map;
  }, [workflow?.tasks]);

  // MEMOIZED: Get the active task run - O(n) but only recalculates when dependencies change
  const activeTaskRun = useMemo(() =>
    taskRuns.find(t => t.id === activeTaskRunId)
  , [taskRuns, activeTaskRunId]);

  // MEMOIZED: Get corresponding workflow task - O(1) using Map lookup
  const activeWorkflowTask = useMemo(() =>
    activeTaskRun ? workflowTaskMap.get(activeTaskRun.task_id) : null
  , [activeTaskRun, workflowTaskMap]);

  // Check if active task is a flow node
  const isActiveTaskFlow = activeWorkflowTask?.type === 'flow';

  /**
   * Auto-trigger AI Flow when a flow node becomes active
   * This handles flow nodes that sit between pages
   */
  useEffect(() => {
    const executeFlow = async () => {
      if (!activeTaskRun || !activeWorkflowTask) return;
      if (activeWorkflowTask.type !== 'flow') return;
      if (activeTaskRun.status !== 'in_progress') return;
      if (!company?.id) return;

      // Don't re-trigger if we're already executing this flow
      if (flowExecutionRef.current === activeTaskRun.id) return;

      const aiFlowConfig = activeWorkflowTask.aiFlowConfig;
      if (!aiFlowConfig?.enabled || !aiFlowConfig?.flowId) {
        // No flow configured - auto-complete the flow node
        console.log('Flow node has no flow configured, auto-completing');
        handleCompleteFlowTask();
        return;
      }

      // Mark this flow as being executed
      flowExecutionRef.current = activeTaskRun.id;

      const mustWait = aiFlowConfig.mustWait ?? false;

      if (mustWait) {
        setIsFlowExecuting(true);
        setFlowExecutionMessage('Running automation...');
        setFlowError(null);
      }

      try {
        // Build payload with friendly field names for reusability across processes
        const webhookUrl = `https://yqmlqctpakqxtwzrdofl.supabase.co/functions/v1/process-webhook/${workflow?.id}/${runId}/${activeTaskRun.task_id}`;
        const friendlyData = buildFriendlyFormData(allFormData, workflow?.tasks);
        const payload = {
          workflow_run_id: runId,
          task_run_id: activeTaskRun.id,
          form_data: friendlyData,
          webhook_trigger_data: workflowRun?.webhook_data || null,
          workflow_name: workflow?.name,
          task_name: activeWorkflowTask.name,
          webhook_url: webhookUrl,
        };

        console.log('Triggering AI Flow:', aiFlowConfig.flowId, 'mustWait:', mustWait, 'fire-and-forget');

        // Always fire-and-forget: trigger the flow without waiting for completion.
        // For mustWait flows, the external system will POST back to webhook_url when done.
        // The auto-advance useEffect detects the webhook callback via Supabase Realtime.
        const result = await triggerFlow(
          company.id,
          aiFlowConfig.flowId,
          payload,
          false // Never wait - use webhook callback instead
        );

        console.log('[Flow] triggerFlow result:', result);

        if (!mustWait) {
          // Non-blocking: flatten any immediate output and auto-complete
          const flatData = flattenFlowResult(result.result);
          console.log('[Flow] Flattened output data:', flatData);
          setFlowOutputData(prev => ({ ...prev, ...flatData }));
          handleCompleteFlowTask();
        } else {
          // Blocking (mustWait): flow has been triggered, now waiting for webhook callback.
          // The loading screen stays visible. When the external system calls the webhook_url,
          // the edge function completes the task, and the auto-advance useEffect picks it up
          // via the existing Realtime subscription on checklists_workflow_task_runs.
          setFlowExecutionMessage('Waiting for automation to complete...');
          console.log('[Flow] mustWait: waiting for webhook callback at', webhookUrl);
        }
      } catch (error) {
        console.error('Error executing flow:', error);
        const errorMsg = error.message || 'Flow execution failed';
        setFlowExecutionMessage(errorMsg);
        setFlowError(errorMsg);
        toast.error('Flow automation failed: ' + errorMsg);
        // Keep isFlowExecuting true so the execution screen stays visible
        flowExecutionRef.current = null;
      }
    };

    executeFlow();
  }, [activeTaskRun?.id, activeTaskRun?.status, activeWorkflowTask?.type, company?.id, flowRetryCount]);

  /**
   * Auto-advance when an external webhook completes the active flow task.
   * Listens for Broadcast events on the webhook-run channel so we don't
   * depend on postgres_changes for the webhook_data column.
   */
  useEffect(() => {
    if (!runId || !isFlowExecuting) return;

    const channel = supabase
      .channel(`webhook-run-${runId}`)
      .on('broadcast', { event: 'webhook_callback' }, (payload) => {
        console.log('[Webhook] Broadcast received:', payload);
        const data = payload.payload;
        if (!data) return;

        // Extract webhook data if present
        if (data.webhook_data) {
          const webhookVars = flattenWebhookData(data.webhook_data);
          console.log('[Webhook] Data received from external webhook:', webhookVars);
          setFlowOutputData(prev => ({ ...prev, ...webhookVars }));
        }

        // Dismiss flow execution loading screen
        setIsFlowExecuting(false);
        setFlowError(null);
        flowExecutionRef.current = null;

        // Refetch task runs to get the updated state from the server
        // The edge function already completed the task and set the next one to in_progress
        const refetchTaskRuns = async () => {
          const { data: updatedRuns } = await supabase
            .from('checklists_workflow_task_runs')
            .select('*')
            .eq('workflow_run_id', runId)
            .order('task_order');
          if (updatedRuns) {
            setTaskRuns(updatedRuns);
            const nextActive = updatedRuns.find(t => t.status === 'in_progress');
            if (nextActive) {
              setActiveTaskRunId(nextActive.id);
            }
          }
        };
        refetchTaskRuns();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [runId, isFlowExecuting]);

  /**
   * MEMOIZED: All form data across all tasks for condition evaluation
   * This allows conditions to reference fields from previous tasks
   * Defined early so downstream hooks can reference it.
   */
  const allFormData = useMemo(() => {
    const data = {};
    taskRuns.forEach(taskRun => {
      if (taskRun.form_data) {
        Object.assign(data, taskRun.form_data);
      }
    });
    return data;
  }, [taskRuns]);

  // Legacy wrapper for compatibility with existing code that expects a function
  const getAllFormData = useCallback(() => allFormData, [allFormData]);

  /**
   * Evaluate a single condition against form data
   */
  const evaluateSingleCondition = useCallback((condition, formData) => {
    const fieldValue = formData?.[condition.fieldId];

    // Handle empty checks
    const isEmpty = fieldValue === undefined || fieldValue === null || fieldValue === '' ||
      (Array.isArray(fieldValue) && fieldValue.length === 0);

    switch (condition.operator) {
      case 'equals':
        return fieldValue === condition.value;
      case 'not_equals':
        return fieldValue !== condition.value;
      case 'contains':
        if (Array.isArray(fieldValue)) {
          return fieldValue.includes(condition.value);
        }
        return String(fieldValue || '').toLowerCase().includes(String(condition.value || '').toLowerCase());
      case 'not_contains':
        if (Array.isArray(fieldValue)) {
          return !fieldValue.includes(condition.value);
        }
        return !String(fieldValue || '').toLowerCase().includes(String(condition.value || '').toLowerCase());
      case 'is_empty':
        return isEmpty;
      case 'is_not_empty':
        return !isEmpty;
      case 'greater_than':
        return Number(fieldValue) > Number(condition.value);
      case 'less_than':
        return Number(fieldValue) < Number(condition.value);
      case 'greater_or_equal':
        return Number(fieldValue) >= Number(condition.value);
      case 'less_or_equal':
        return Number(fieldValue) <= Number(condition.value);
      case 'before':
        return new Date(fieldValue) < new Date(condition.value);
      case 'after':
        return new Date(fieldValue) > new Date(condition.value);
      default:
        return true;
    }
  }, []);

  /**
   * Evaluate a group of conditions (supports nested groups)
   */
  const evaluateConditionGroup = useCallback((group, formData, allFields) => {
    const conditions = group.conditions || [];
    const innerGroups = group.groups || [];

    // Evaluate all conditions in this group
    const conditionResults = conditions
      .filter(c => c.fieldId) // Skip incomplete conditions
      .map(c => evaluateSingleCondition(c, formData));

    // Evaluate all inner groups
    const groupResults = innerGroups.map(g => evaluateConditionGroup(g, formData, allFields));

    // Combine all results
    const allResults = [...conditionResults, ...groupResults];

    if (allResults.length === 0) return true; // No conditions = show

    // Apply match type
    if (group.matchType === 'any') {
      return allResults.some(r => r);
    } else {
      // 'all' is default
      return allResults.every(r => r);
    }
  }, [evaluateSingleCondition]);

  /**
   * Evaluate conditional logic for a field and determine if it should be visible
   */
  const shouldShowField = useCallback((field, formData, allFields) => {
    const conditionalLogic = field.conditionalLogic;

    // No conditional logic or disabled = always show
    if (!conditionalLogic || conditionalLogic.mode === 'disabled') {
      return true;
    }

    // Build a root group from the conditional logic
    const rootGroup = {
      matchType: conditionalLogic.matchType || 'all',
      conditions: conditionalLogic.conditions || [],
      groups: conditionalLogic.groups || []
    };

    const conditionsMatch = evaluateConditionGroup(rootGroup, formData, allFields);

    // Apply mode: show_when means show if conditions match, hide_when means hide if conditions match
    if (conditionalLogic.mode === 'show_when') {
      return conditionsMatch;
    } else if (conditionalLogic.mode === 'hide_when') {
      return !conditionsMatch;
    }

    return true;
  }, [evaluateConditionGroup]);

  /**
   * MEMOIZED: All fields from all tasks for condition evaluation
   * Pre-computed once when workflow changes, not on every render
   */
  const allFields = useMemo(() => {
    const fields = {};
    workflow?.tasks?.forEach(task => {
      (task.content || []).forEach(item => {
        if (item.category === 'form') {
          fields[item.id] = item;
        }
      });
    });
    return fields;
  }, [workflow?.tasks]);

  // Legacy wrapper for compatibility with existing code that expects a function
  const getAllFields = useCallback(() => allFields, [allFields]);

  /**
   * Evaluate task-level conditional logic to determine if a task should be shown
   */
  const shouldShowTask = useCallback((workflowTask, formData, fieldsMap = null) => {
    if (!workflowTask) return true;

    const conditionalLogic = workflowTask.conditionalLogic;

    // No conditional logic or disabled = always show
    if (!conditionalLogic || conditionalLogic.mode === 'disabled') {
      return true;
    }

    // Build a root group from the conditional logic
    const rootGroup = {
      matchType: conditionalLogic.matchType || 'all',
      conditions: conditionalLogic.conditions || [],
      groups: conditionalLogic.groups || []
    };

    // Use provided fieldsMap or fall back to memoized allFields
    const fields = fieldsMap || allFields;
    const conditionsMatch = evaluateConditionGroup(rootGroup, formData, fields);

    // Apply mode: show_when means show if conditions match, hide_when means hide if conditions match
    if (conditionalLogic.mode === 'show_when') {
      return conditionsMatch;
    } else if (conditionalLogic.mode === 'hide_when') {
      return !conditionsMatch;
    }

    return true;
  }, [evaluateConditionGroup, allFields]);

  /**
   * Complete a flow task (auto-triggered, no form validation needed)
   */
  const handleCompleteFlowTask = useCallback(async () => {
    if (!activeTaskRun || !userProfile) return;
    // Don't complete if the task/run was cancelled while the flow was executing
    if (activeTaskRun.status === 'cancelled' || activeTaskRun.status === 'completed') return;

    try {
      const currentIndex = taskRuns.findIndex(t => t.id === activeTaskRun.id);
      const mergedFormData = { ...allFormData };

      // Find next visible pending task
      let nextTask = null;
      for (let i = currentIndex + 1; i < taskRuns.length; i++) {
        const candidateTask = taskRuns[i];
        const workflowTask = workflowTaskMap.get(candidateTask.task_id);
        if (shouldShowTask(workflowTask, mergedFormData)) {
          nextTask = candidateTask;
          break;
        }
      }

      const existingTaskRunIds = new Set(taskRuns.map(t => t.task_id));
      const hasUncreatedTasks = workflow?.tasks?.some((t, index) =>
        index > activeTaskRun.task_order && !existingTaskRunIds.has(String(t.id))
      );

      let tasksToCreate = null;
      let completeWorkflow = false;
      let nextTaskId = nextTask?.id || null;

      // If no next task exists, check for uncreated tasks
      if (!nextTask && hasUncreatedTasks && workflow?.tasks) {
        const allWorkflowTasks = workflow.tasks;
        const nextTasksToCreate = [];

        for (let i = activeTaskRun.task_order + 1; i < allWorkflowTasks.length; i++) {
          const task = allWorkflowTasks[i];
          if (existingTaskRunIds.has(String(task.id))) continue;
          const shouldShow = shouldShowTask(task, mergedFormData);
          if (!shouldShow) continue;

          nextTasksToCreate.push({
            task_id: String(task.id),
            task_order: i,
            status: nextTasksToCreate.length === 0 ? 'in_progress' : 'pending',
            assignees: task.assignees || [],
            due_date: task.dueDate ? calculateDueDate(task.dueDate) : null,
          });

          if (task.stopTask) break;
        }

        if (nextTasksToCreate.length > 0) {
          tasksToCreate = nextTasksToCreate;
        } else {
          completeWorkflow = true;
        }
      } else if (!nextTask) {
        completeWorkflow = true;
      }

      // Complete the flow task
      const { data: result, error } = await supabase.rpc('complete_workflow_task', {
        p_task_run_id: activeTaskRun.id,
        p_user_id: userProfile.id,
        p_form_data: null, // Flow nodes don't have form data
        p_next_task_id: nextTaskId,
        p_tasks_to_create: tasksToCreate,
        p_complete_workflow: completeWorkflow
      });

      if (error) throw error;

      const createdTaskRuns = result?.created_task_runs || [];
      createdTaskRuns.forEach(tr => locallyInsertedIdsRef.current.add(tr.id));

      const completedAt = result?.completed_at;

      if (createdTaskRuns.length > 0) {
        setTaskRuns(prev => {
          const existingIds = new Set(prev.map(t => t.id));
          const uniqueNewTasks = createdTaskRuns.filter(t => !existingIds.has(t.id));
          return [
            ...prev.map(t =>
              t.id === activeTaskRun.id
                ? { ...t, status: 'completed', completed_at: completedAt, completed_by: userProfile.id }
                : t
            ),
            ...uniqueNewTasks
          ];
        });
        setActiveTaskRunId(createdTaskRuns[0].id);
      } else if (nextTaskId) {
        setTaskRuns(prev => prev.map(t => {
          if (t.id === activeTaskRun.id) return { ...t, status: 'completed', completed_at: completedAt, completed_by: userProfile.id };
          if (t.id === nextTaskId) return { ...t, status: 'in_progress' };
          return t;
        }));
        setActiveTaskRunId(nextTaskId);
      } else if (completeWorkflow) {
        setTaskRuns(prev => prev.map(t =>
          t.id === activeTaskRun.id
            ? { ...t, status: 'completed', completed_at: completedAt, completed_by: userProfile.id }
            : t
        ));
        setWorkflowRun(prev => ({ ...prev, status: 'completed', completed_at: completedAt }));
        toast.success('Workflow completed!');
      }
    } catch (error) {
      console.error('Error completing flow task:', error);
      toast.error('Failed to complete automation step');
    }
  }, [activeTaskRun, userProfile, taskRuns, allFormData, workflowTaskMap, shouldShowTask, workflow?.tasks]);

  // MEMOIZED: Calculate progress - only recalculates when taskRuns change
  const { completedCount, progressPercent } = useMemo(() => {
    const completed = taskRuns.filter(t => t.status === 'completed').length;
    return {
      completedCount: completed,
      progressPercent: taskRuns.length > 0 ? (completed / taskRuns.length) * 100 : 0
    };
  }, [taskRuns]);

  // Debounced save to database
  const saveFormDataToDb = useCallback(async (taskRunId, formData) => {
    console.log('Saving form data to DB:', { taskRunId, formData });
    try {
      const { data, error } = await supabase
        .from('checklists_workflow_task_runs')
        .update({ form_data: formData, updated_date: new Date().toISOString() })
        .eq('id', taskRunId)
        .select();

      if (error) {
        console.error('Error saving form data:', error);
        toast.error('Failed to save changes');
      } else {
        console.log('Form data saved successfully:', data);
      }
    } catch (error) {
      console.error('Exception saving form data:', error);
      toast.error('Failed to save changes');
    }
  }, []);

  // Handle form field changes with debouncing
  const handleFormChange = useCallback((fieldId, value) => {
    if (!activeTaskRun) return;
    // Prevent changes to completed or cancelled tasks
    if (activeTaskRun.status === 'completed' || activeTaskRun.status === 'cancelled') return;

    const taskRunId = activeTaskRun.id;

    // Build updated form data from current state + new change
    const currentFormData = pendingChangesRef.current[taskRunId] || activeTaskRun.form_data || {};
    const updatedFormData = {
      ...currentFormData,
      [fieldId]: value,
    };

    // Store pending changes
    pendingChangesRef.current[taskRunId] = updatedFormData;

    // Optimistic update - update UI immediately
    setTaskRuns(prev => prev.map(t =>
      t.id === taskRunId
        ? { ...t, form_data: updatedFormData }
        : t
    ));

    // Clear existing debounce timer for this task run
    if (debounceTimerRef.current[taskRunId]) {
      clearTimeout(debounceTimerRef.current[taskRunId]);
    }

    // Set new debounce timer (500ms delay)
    debounceTimerRef.current[taskRunId] = setTimeout(() => {
      const dataToSave = pendingChangesRef.current[taskRunId];
      if (dataToSave) {
        saveFormDataToDb(taskRunId, dataToSave);
        // Clear pending changes after save
        delete pendingChangesRef.current[taskRunId];
      }
      delete debounceTimerRef.current[taskRunId];
    }, 500);
  }, [activeTaskRun, saveFormDataToDb]);

  // Cleanup debounce timers on unmount and save any pending changes
  useEffect(() => {
    return () => {
      // Save any pending changes before unmount
      Object.entries(pendingChangesRef.current).forEach(([taskRunId, formData]) => {
        saveFormDataToDb(taskRunId, formData);
      });
      // Clear all timers
      Object.values(debounceTimerRef.current).forEach(timer => clearTimeout(timer));
    };
  }, [saveFormDataToDb]);

  // Handle subtask toggle
  const handleSubtaskToggle = async (fieldId, subtaskId, completed) => {
    if (!activeTaskRun) return;

    const currentSubtasks = activeTaskRun.form_data?.[fieldId] || {};
    const updatedSubtasks = {
      ...currentSubtasks,
      [subtaskId]: completed,
    };

    handleFormChange(fieldId, updatedSubtasks);
  };

  /**
   * OPTIMIZED: Get visible task runs (filtered by task-level conditional logic)
   * Uses pre-computed workflowTaskMap for O(1) lookups instead of O(n) .find()
   * Completed/cancelled tasks are always shown regardless of conditions
   */
  const visibleTaskRuns = useMemo(() => {
    return taskRuns.filter(taskRun => {
      // Always show completed or cancelled tasks
      if (taskRun.status === 'completed' || taskRun.status === 'cancelled') {
        return true;
      }

      // O(1) lookup using Map instead of O(n) .find()
      const workflowTask = workflowTaskMap.get(taskRun.task_id);

      // Evaluate task-level conditional logic with pre-computed values
      return shouldShowTask(workflowTask, allFormData, allFields);
    });
  }, [taskRuns, workflowTaskMap, allFormData, allFields, shouldShowTask]);

  // Ensure active task is always visible - if current active task becomes hidden, select next visible
  useEffect(() => {
    if (!activeTaskRunId || visibleTaskRuns.length === 0) return;

    const isActiveVisible = visibleTaskRuns.some(t => t.id === activeTaskRunId);
    if (!isActiveVisible) {
      // Find the next visible task that's not completed/cancelled
      const nextActive = visibleTaskRuns.find(t => t.status === 'in_progress' || t.status === 'pending');
      if (nextActive) {
        setActiveTaskRunId(nextActive.id);
      } else if (visibleTaskRuns.length > 0) {
        // Fall back to first visible task
        setActiveTaskRunId(visibleTaskRuns[0].id);
      }
    }
  }, [activeTaskRunId, visibleTaskRuns]);

  // Helper to calculate due date based on task settings
  const calculateDueDate = (dueDateConfig) => {
    if (!dueDateConfig) return null;

    const now = new Date();
    let dueDate = new Date(now);

    if (dueDateConfig.months) dueDate.setMonth(dueDate.getMonth() + dueDateConfig.months);
    if (dueDateConfig.days) dueDate.setDate(dueDate.getDate() + dueDateConfig.days);
    if (dueDateConfig.hours) dueDate.setHours(dueDate.getHours() + dueDateConfig.hours);
    if (dueDateConfig.minutes) dueDate.setMinutes(dueDate.getMinutes() + dueDateConfig.minutes);

    if (dueDateConfig.businessDayHandling === 'shift_next' || dueDateConfig.businessDayHandling === 'business_only') {
      const day = dueDate.getDay();
      if (day === 0) dueDate.setDate(dueDate.getDate() + 1);
      if (day === 6) dueDate.setDate(dueDate.getDate() + 2);
    }

    return dueDate.toISOString();
  };

  // Complete current task and move to next
  // Uses atomic RPC to handle all database operations in a single transaction
  const handleCompleteTask = async () => {
    if (!activeTaskRun || !userProfile) return;

    // Validate required fields before completing (only check visible fields)
    // Use O(1) Map lookup instead of O(n) .find()
    const currentWorkflowTaskForValidation = workflowTaskMap.get(activeTaskRun.task_id);
    if (currentWorkflowTaskForValidation?.content) {
      const formData = activeTaskRun.form_data || {};
      const currentAllFormData = getAllFormData();
      const currentAllFields = getAllFields();

      // Filter to only visible form fields based on conditional logic
      const visibleFormFields = currentWorkflowTaskForValidation.content.filter(item =>
        item.category === 'form' && shouldShowField(item, currentAllFormData, currentAllFields)
      );
      const requiredFields = visibleFormFields.filter(field => field.content?.required);

      const missingFields = requiredFields.filter(field => {
        const value = formData[field.id];
        // Check for empty values based on field type
        if (value === undefined || value === null || value === '') return true;
        if (Array.isArray(value) && value.length === 0) return true;
        if (typeof value === 'object' && !Array.isArray(value)) {
          // For objects like User fields or File uploads, check for required properties
          if (field.label === 'User') return !value.id;
          if (field.label === 'File Upload') return !value.url;
          if (field.label === 'Table') return !value.length || value.length === 0;
        }
        return false;
      });

      if (missingFields.length > 0) {
        const fieldNames = missingFields.map(f => f.content?.fieldName || f.label).join(', ');
        toast.error(`Please complete required fields: ${fieldNames}`);
        return;
      }
    }

    setIsSaving(true);

    try {
      // Prepare data for atomic RPC call
      const currentTaskOrder = activeTaskRun.task_order;

      // Include current task's form_data for conditional logic evaluation
      // Use memoized allFormData as base, add current task's latest form_data
      const mergedFormData = {
        ...allFormData,
        ...(activeTaskRun.form_data || {})
      };
      const currentIndex = taskRuns.findIndex(t => t.id === activeTaskRun.id);

      // Find the next VISIBLE pending task in current task runs
      // Use O(1) Map lookup instead of O(n) .find()
      let nextTask = null;
      for (let i = currentIndex + 1; i < taskRuns.length; i++) {
        const candidateTask = taskRuns[i];
        const workflowTask = workflowTaskMap.get(candidateTask.task_id);
        if (shouldShowTask(workflowTask, mergedFormData)) {
          nextTask = candidateTask;
          break;
        }
      }

      // Determine what needs to happen
      const existingTaskRunIds = new Set(taskRuns.map(t => t.task_id));
      const hasUncreatedTasks = workflow?.tasks?.some((t, index) =>
        index > currentTaskOrder && !existingTaskRunIds.has(String(t.id))
      );

      let tasksToCreate = null;
      let completeWorkflow = false;
      let nextTaskId = nextTask?.id || null;

      // If no next task exists, check for uncreated tasks
      if (!nextTask && hasUncreatedTasks && workflow?.tasks) {
        const allWorkflowTasks = workflow.tasks;
        const nextTasksToCreate = [];

        for (let i = currentTaskOrder + 1; i < allWorkflowTasks.length; i++) {
          const task = allWorkflowTasks[i];

          // Skip if this task already has a task run created
          if (existingTaskRunIds.has(String(task.id))) {
            continue;
          }

          // Check if this task should be shown based on conditional logic
          const shouldShow = shouldShowTask(task, mergedFormData);
          if (!shouldShow) continue;

          nextTasksToCreate.push({
            task_id: String(task.id),
            task_order: i,
            status: nextTasksToCreate.length === 0 ? 'in_progress' : 'pending',
            assignees: task.assignees || [],
            due_date: task.dueDate ? calculateDueDate(task.dueDate) : null,
          });

          // If this task has stopTask enabled, don't create any more
          if (task.stopTask) break;
        }

        if (nextTasksToCreate.length > 0) {
          tasksToCreate = nextTasksToCreate;
        } else {
          completeWorkflow = true;
        }
      } else if (!nextTask) {
        // No next task and no uncreated tasks - workflow is complete
        completeWorkflow = true;
      }

      // Single atomic RPC call handles all database operations
      const { data: result, error } = await supabase.rpc('complete_workflow_task', {
        p_task_run_id: activeTaskRun.id,
        p_user_id: userProfile.id,
        p_form_data: activeTaskRun.form_data || null,
        p_next_task_id: nextTaskId,
        p_tasks_to_create: tasksToCreate,
        p_complete_workflow: completeWorkflow
      });

      if (error) throw error;

      // Trigger page-level AI flow if configured (non-blocking, fire-and-forget)
      const pageAiFlowConfig = currentWorkflowTaskForValidation?.aiFlowConfig;
      if (pageAiFlowConfig?.enabled && pageAiFlowConfig?.flowId && company?.id) {
        // Build payload from form data, include webhook callback URL
        const pageWebhookUrl = `https://yqmlqctpakqxtwzrdofl.supabase.co/functions/v1/process-webhook/${workflow?.id}/${runId}/${activeTaskRun.task_id}`;
        const flowPayload = {
          workflow_run_id: runId,
          task_run_id: activeTaskRun.id,
          form_data: buildFriendlyFormData(mergedFormData, workflow?.tasks),
          workflow_name: workflow?.name,
          task_name: currentWorkflowTaskForValidation?.name || currentWorkflowTaskForValidation?.formTitle,
          trigger_type: 'page_completion',
          webhook_url: pageWebhookUrl,
        };

        // Fire-and-forget - don't await, don't block completion
        triggerFlow(company.id, pageAiFlowConfig.flowId, flowPayload, false)
          .then(result => console.log('Page AI flow triggered:', result))
          .catch(err => console.error('Error triggering page AI flow:', err));
      }

      // Track locally-inserted IDs to prevent duplicates from real-time subscription
      const createdTaskRuns = result?.created_task_runs || [];
      createdTaskRuns.forEach(tr => locallyInsertedIdsRef.current.add(tr.id));

      // Update local state based on RPC result
      const completedAt = result?.completed_at;

      if (createdTaskRuns.length > 0) {
        // New tasks were created
        setTaskRuns(prev => {
          const existingIds = new Set(prev.map(t => t.id));
          const uniqueNewTasks = createdTaskRuns.filter(t => !existingIds.has(t.id));
          return [
            ...prev.map(t =>
              t.id === activeTaskRun.id
                ? { ...t, status: 'completed', completed_at: completedAt, completed_by: userProfile.id }
                : t
            ),
            ...uniqueNewTasks
          ];
        });

        // Set the first new task as active
        setActiveTaskRunId(createdTaskRuns[0].id);
        toast.success('Task completed!');
      } else if (nextTaskId) {
        // Existing next task was activated
        setTaskRuns(prev => prev.map(t => {
          if (t.id === activeTaskRun.id) return { ...t, status: 'completed', completed_at: completedAt, completed_by: userProfile.id };
          if (t.id === nextTaskId) return { ...t, status: 'in_progress' };
          return t;
        }));

        setActiveTaskRunId(nextTaskId);
        toast.success('Task completed!');
      } else if (completeWorkflow) {
        // Workflow was completed
        setTaskRuns(prev => prev.map(t =>
          t.id === activeTaskRun.id
            ? { ...t, status: 'completed', completed_at: completedAt, completed_by: userProfile.id }
            : t
        ));

        setWorkflowRun(prev => ({ ...prev, status: 'completed', completed_at: completedAt }));
        toast.success('Workflow completed!');
      }
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task');
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel the workflow run
  const handleCancelRun = async () => {
    if (!workflowRun || !userProfile) return;

    // Confirm cancellation
    if (!window.confirm('Are you sure you want to cancel this workflow run? This action cannot be undone.')) {
      return;
    }

    setIsCancelling(true);

    // Clear any pending debounced saves to prevent false "Failed to save" toasts
    Object.values(debounceTimerRef.current).forEach(timer => clearTimeout(timer));
    debounceTimerRef.current = {};
    pendingChangesRef.current = {};

    // Clear flow execution state to prevent flow completion on a cancelled run
    flowExecutionRef.current = null;
    setIsFlowExecuting(false);

    try {
      // Update the workflow run status to cancelled
      const { error: runError } = await supabase
        .from('checklists_workflow_runs')
        .update({
          status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          cancelled_by: userProfile.id,
          updated_date: new Date().toISOString(),
        })
        .eq('id', runId);

      if (runError) throw runError;

      // Update all pending and in_progress task runs to cancelled
      const taskRunIdsToCancel = taskRuns
        .filter(t => t.status === 'pending' || t.status === 'in_progress')
        .map(t => t.id);

      if (taskRunIdsToCancel.length > 0) {
        const { error: taskError } = await supabase
          .from('checklists_workflow_task_runs')
          .update({
            status: 'cancelled',
            updated_date: new Date().toISOString(),
          })
          .in('id', taskRunIdsToCancel);

        if (taskError) throw taskError;
      }

      // Update local state
      setWorkflowRun(prev => ({
        ...prev,
        status: 'cancelled',
        cancelled_at: new Date().toISOString(),
        cancelled_by: userProfile.id,
      }));

      setTaskRuns(prev => prev.map(t =>
        t.status === 'pending' || t.status === 'in_progress'
          ? { ...t, status: 'cancelled' }
          : t
      ));

      toast.success('Workflow run cancelled');
    } catch (error) {
      console.error('Error cancelling workflow run:', error);
      toast.error('Failed to cancel workflow run');
    } finally {
      setIsCancelling(false);
    }
  };

  // Update task run assignees
  const handleUpdateTaskAssignees = async (taskRunId, newAssignees) => {
    try {
      const { error } = await supabase
        .from('checklists_workflow_task_runs')
        .update({
          assignees: newAssignees,
          updated_date: new Date().toISOString(),
        })
        .eq('id', taskRunId);

      if (error) throw error;

      // Update local state
      setTaskRuns(prev => prev.map(t =>
        t.id === taskRunId ? { ...t, assignees: newAssignees } : t
      ));

      toast.success('Assignees updated');
    } catch (error) {
      console.error('Error updating assignees:', error);
      toast.error('Failed to update assignees');
    }
  };

  // Toggle assignee on active task
  const handleToggleAssignee = (memberId) => {
    if (!activeTaskRun) return;

    const currentAssignees = Array.isArray(activeTaskRun.assignees)
      ? activeTaskRun.assignees
      : [];

    let newAssignees;
    if (currentAssignees.includes(memberId)) {
      newAssignees = currentAssignees.filter(id => id !== memberId);
    } else {
      newAssignees = [...currentAssignees, memberId];
    }

    handleUpdateTaskAssignees(activeTaskRun.id, newAssignees);
  };

  // Assign all team members to active task
  const handleAssignAllToTask = () => {
    if (!activeTaskRun) return;
    const allMemberIds = teamMembers.map(m => m.id);
    handleUpdateTaskAssignees(activeTaskRun.id, allMemberIds);
  };

  // Clear all assignees from active task
  const handleClearAssignees = () => {
    if (!activeTaskRun) return;
    handleUpdateTaskAssignees(activeTaskRun.id, []);
  };

  // Render form field based on type
  // Whether the currently viewed task is read-only (completed or cancelled)
  const isTaskReadOnly = activeTaskRun?.status === 'completed' || activeTaskRun?.status === 'cancelled';

  const renderFormField = (item) => {
    const fieldId = item.id;
    const value = activeTaskRun?.form_data?.[fieldId] || '';
    const fieldName = item.content?.fieldName || item.label;
    const isRequired = item.content?.required;

    // Helper to render label with required indicator and variable replacement
    const renderLabel = (name) => {
      const processedName = replaceVariablesInContent(name, { ...allFormData, ...flowOutputData }, allFields);
      return (
        <label className="text-sm font-medium">
          <span dangerouslySetInnerHTML={{ __html: processedName }} />
          {isRequired && <span className="text-red-500 ml-1">*</span>}
        </label>
      );
    };

    switch (item.label) {
      case 'Short Text':
        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            <Input
              value={value}
              onChange={(e) => handleFormChange(fieldId, e.target.value)}
              placeholder={`Enter ${fieldName.toLowerCase()}`}
              disabled={isTaskReadOnly}
            />
          </div>
        );

      case 'Long Text':
        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            <Textarea
              value={value}
              onChange={(e) => handleFormChange(fieldId, e.target.value)}
              placeholder={`Enter ${fieldName.toLowerCase()}`}
              rows={4}
              disabled={isTaskReadOnly}
            />
          </div>
        );

      case 'Email':
        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            <Input
              type="email"
              value={value}
              onChange={(e) => handleFormChange(fieldId, e.target.value)}
              placeholder="Enter email address"
              disabled={isTaskReadOnly}
            />
          </div>
        );

      case 'Website':
        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            <Input
              type="url"
              value={value}
              onChange={(e) => handleFormChange(fieldId, e.target.value)}
              placeholder="https://..."
              disabled={isTaskReadOnly}
            />
          </div>
        );

      case 'Numbers':
        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            <Input
              type="number"
              value={value}
              onChange={(e) => handleFormChange(fieldId, e.target.value)}
              placeholder="Enter number"
              disabled={isTaskReadOnly}
            />
          </div>
        );

      case 'Date':
        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            <Input
              type="date"
              value={value}
              onChange={(e) => handleFormChange(fieldId, e.target.value)}
              disabled={isTaskReadOnly}
            />
          </div>
        );

      case 'User':
        const selectedUser = value ? (typeof value === 'string' ? { id: value } : value) : null;
        const selectedUserData = selectedUser?.id ? teamMembers.find(u => u.id === selectedUser.id) : null;

        const searchTeamMembers = async (query) => {
          const filtered = teamMembers.filter(user => {
            const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
            const email = (user.email || '').toLowerCase();
            const searchTerm = query.toLowerCase();
            return fullName.includes(searchTerm) || email.includes(searchTerm);
          });

          return filtered.map(user => ({
            value: user.id,
            label: `${user.first_name} ${user.last_name}`,
            data: user,
          }));
        };

        const getDisplayLabel = () => {
          if (selectedUser?.first_name && selectedUser?.last_name) {
            return `${selectedUser.first_name} ${selectedUser.last_name}`;
          }
          if (selectedUserData) {
            return `${selectedUserData.first_name} ${selectedUserData.last_name}`;
          }
          return 'Select a team member...';
        };

        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            <AsyncCombobox
              value={selectedUser?.id || ''}
              onValueChange={(newValue) => {
                if (!newValue) {
                  handleFormChange(fieldId, null);
                }
              }}
              onSelectionChange={(userData) => {
                handleFormChange(fieldId, {
                  id: userData.id,
                  first_name: userData.first_name,
                  last_name: userData.last_name,
                  email: userData.email,
                  avatar_url: userData.avatar_url,
                });
              }}
              onSearch={searchTeamMembers}
              placeholder="Select a team member..."
              emptyText="No team members found"
              displayLabel={getDisplayLabel()}
              disabled={isTaskReadOnly}
            />
            {selectedUser?.id && selectedUserData && (
              <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={selectedUserData.avatar_url} alt={`${selectedUserData.first_name} ${selectedUserData.last_name}`} />
                  <AvatarFallback>
                    {selectedUserData.first_name?.[0]}{selectedUserData.last_name?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="font-medium">{selectedUserData.first_name} {selectedUserData.last_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedUserData.email}</p>
                </div>
                {!isTaskReadOnly && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleFormChange(fieldId, null)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            )}
          </div>
        );

      case 'Dropdown':
        return (
          <DynamicDropdown
            item={item}
            value={value}
            onChange={(val) => handleFormChange(fieldId, val)}
            renderLabel={renderLabel}
            fieldName={fieldName}
            companyId={company?.id}
            disabled={isTaskReadOnly}
          />
        );

      case 'Multi Choice':
        const multiOptions = item.content?.options || [];
        const selectedValues = value ? (Array.isArray(value) ? value : [value]) : [];
        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            <div className="space-y-2">
              {multiOptions.map((option) => (
                <label key={option.id} className={`flex items-center gap-2 ${isTaskReadOnly ? 'cursor-default' : 'cursor-pointer'}`}>
                  <Checkbox
                    checked={selectedValues.includes(option.value)}
                    onCheckedChange={(checked) => {
                      const newValues = checked
                        ? [...selectedValues, option.value]
                        : selectedValues.filter(v => v !== option.value);
                      handleFormChange(fieldId, newValues);
                    }}
                    disabled={isTaskReadOnly}
                  />
                  <span>{option.value}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'File Upload':
        const uploadedFile = value ? (typeof value === 'string' ? { url: value } : value) : null;
        const isUploading = uploadingFields[fieldId];

        const handleFileUpload = async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;

          // Max 50MB (same as other file uploads)
          if (file.size > 50 * 1024 * 1024) {
            toast.error('File size must be less than 50MB');
            return;
          }

          setUploadingFields(prev => ({ ...prev, [fieldId]: true }));

          try {
            // Generate a unique filename
            const timestamp = Date.now();
            const originalName = file.name;
            const fileExtension = originalName.split('.').pop();
            const fileName = `${timestamp}-${Math.random().toString(36).substring(7)}.${fileExtension}`;

            // Get signed upload URL from edge function (uploads to Google Cloud Storage)
            const { data: urlData, error: urlError } = await supabase.functions.invoke('generate-upload-url', {
              body: {
                action: 'upload',
                fileName: fileName,
                contentType: file.type || 'application/octet-stream',
                folderPath: `workflow-runs/${runId}/${fieldId}`,
              },
            });

            if (urlError) throw urlError;

            // Upload file to GCS using signed URL
            const uploadResponse = await fetch(urlData.signedUrl, {
              method: 'PUT',
              headers: {
                'Content-Type': file.type || 'application/octet-stream',
              },
              body: file,
            });

            if (!uploadResponse.ok) {
              throw new Error('Failed to upload file to storage');
            }

            // Save file info to form data
            handleFormChange(fieldId, {
              url: urlData.publicUrl,
              name: originalName,
              size: file.size,
              type: file.type,
            });

            toast.success('File uploaded successfully');
          } catch (error) {
            console.error('Upload error:', error);
            toast.error('Failed to upload file: ' + error.message);
          } finally {
            setUploadingFields(prev => ({ ...prev, [fieldId]: false }));
          }
        };

        const handleRemoveFile = () => {
          handleFormChange(fieldId, null);
        };

        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            {uploadedFile?.url ? (
              <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <File className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">{uploadedFile.name || 'Uploaded file'}</p>
                    {uploadedFile.size && (
                      <p className="text-sm text-muted-foreground">
                        {(uploadedFile.size / 1024).toFixed(1)} KB
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(uploadedFile.url, '_blank')}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    View
                  </Button>
                  {!isTaskReadOnly && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveFile}
                      className="text-destructive hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <label className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors block ${isTaskReadOnly ? 'cursor-default opacity-50' : 'cursor-pointer hover:border-primary hover:bg-muted/50'}`}>
                <input
                  type="file"
                  className="hidden"
                  onChange={handleFileUpload}
                  disabled={isUploading || isTaskReadOnly}
                />
                {isUploading ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-8 w-8 text-primary animate-spin" />
                    <span className="text-sm text-muted-foreground">Uploading...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="h-8 w-8 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Click to upload a file
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Max 50MB
                    </span>
                  </div>
                )}
              </label>
            )}
          </div>
        );

      case 'Signature':
        return (
          <SignatureCapture
            value={value}
            onChange={(newValue) => handleFormChange(fieldId, newValue)}
            fieldName={fieldName}
            renderLabel={renderLabel}
            disabled={isTaskReadOnly}
          />
        );

      case 'Table':
        const tableColumns = item.content?.columns || [];
        let tableRows = value || [];

        // Auto-insert first row if table is empty and has columns
        if (tableRows.length === 0 && tableColumns.length > 0) {
          const initialRow = { id: Date.now() };
          tableColumns.forEach(col => {
            initialRow[col.id] = '';
          });
          tableRows = [initialRow];
          // Save the initial row to form data
          setTimeout(() => handleFormChange(fieldId, tableRows), 0);
        }

        const addTableRow = () => {
          const newRow = { id: Date.now() };
          tableColumns.forEach(col => {
            newRow[col.id] = '';
          });
          handleFormChange(fieldId, [...tableRows, newRow]);
        };

        const updateTableCell = (rowId, columnId, cellValue) => {
          const updatedRows = tableRows.map(row =>
            row.id === rowId ? { ...row, [columnId]: cellValue } : row
          );
          handleFormChange(fieldId, updatedRows);
        };

        const removeTableRow = (rowId) => {
          const updatedRows = tableRows.filter(row => row.id !== rowId);
          handleFormChange(fieldId, updatedRows);
        };

        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            {tableColumns.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted/50">
                      {tableColumns.map(col => (
                        <th key={col.id} className="px-3 py-2 text-left text-sm font-medium border-b">
                          {col.name || 'Column'}
                        </th>
                      ))}
                      {!isTaskReadOnly && <th className="w-10 border-b"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row, rowIndex) => (
                      <tr key={row.id} className="border-b last:border-b-0">
                        {tableColumns.map(col => (
                          <td key={col.id} className="p-1">
                            <Input
                              value={row[col.id] || ''}
                              onChange={(e) => updateTableCell(row.id, col.id, e.target.value)}
                              className="h-8 text-sm border-0 focus-visible:ring-1"
                              placeholder="Enter value..."
                              disabled={isTaskReadOnly}
                            />
                          </td>
                        ))}
                        {!isTaskReadOnly && (
                          <td className="p-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeTableRow(row.id)}
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {tableRows.length === 0 && (
                      <tr>
                        <td colSpan={tableColumns.length + 1} className="p-4 text-center text-sm text-muted-foreground">
                          No rows yet. Click "Add Row" to start.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {!isTaskReadOnly && (
                  <div className="p-2 bg-muted/30 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addTableRow}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Row
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 border rounded-lg text-center text-sm text-muted-foreground">
                No columns configured for this table
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="space-y-2">
            {renderLabel(fieldName)}
            <Input
              value={value}
              onChange={(e) => handleFormChange(fieldId, e.target.value)}
              placeholder={`Enter ${fieldName.toLowerCase()}`}
              disabled={isTaskReadOnly}
            />
          </div>
        );
    }
  };

  // Render content item (non-form items like text, images, etc.)
  const renderContentItem = (item) => {
    switch (item.label) {
      case 'Text':
        // Replace variables like {{ fieldId }} with actual form data values
        const processedContent = replaceVariablesInContent(
          item.content,
          { ...allFormData, ...flowOutputData },
          allFields
        );
        return (
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: processedContent }}
          />
        );

      case 'Image':
        const imageDataRaw = typeof item.content === 'string'
          ? { url: item.content }
          : item.content;
        const resolvedImageUrl = replaceVariablesInContent(imageDataRaw?.url || '', { ...allFormData, ...flowOutputData }, allFields).replace(/<[^>]+>/g, '');
        return resolvedImageUrl ? (
          <div className="space-y-2">
            <img
              src={resolvedImageUrl}
              alt={imageDataRaw?.description || 'Image'}
              className="w-full rounded-lg"
            />
            {imageDataRaw?.description && (
              <p className="text-sm text-muted-foreground italic">{imageDataRaw.description}</p>
            )}
          </div>
        ) : null;

      case 'Video':
        const videoDataRaw = typeof item.content === 'string'
          ? { url: item.content }
          : item.content;
        const resolvedVideoUrl = replaceVariablesInContent(videoDataRaw?.url || '', { ...allFormData, ...flowOutputData }, allFields).replace(/<[^>]+>/g, '');
        if (!resolvedVideoUrl) return null;

        const isEmbed = resolvedVideoUrl.includes('youtube.com') || resolvedVideoUrl.includes('vimeo.com');
        return (
          <div className="space-y-2">
            {isEmbed ? (
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={resolvedVideoUrl}
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                  frameBorder="0"
                  allowFullScreen
                />
              </div>
            ) : (
              <video src={resolvedVideoUrl} controls className="w-full rounded-lg" />
            )}
            {videoDataRaw?.description && (
              <p className="text-sm text-muted-foreground italic">{videoDataRaw.description}</p>
            )}
          </div>
        );

      case 'File':
        const fileDataRaw = typeof item.content === 'string'
          ? { url: item.content, name: 'Download' }
          : item.content;

        // Resolve variables in file URL and name
        const mergedVarData = { ...allFormData, ...flowOutputData };
        const resolvedFileUrl = replaceVariablesInContent(fileDataRaw?.url || '', mergedVarData, allFields)
          .replace(/<[^>]+>/g, '');
        const resolvedFileName = replaceVariablesInContent(fileDataRaw?.name || 'File', mergedVarData, allFields)
          .replace(/<[^>]+>/g, '');
        const fileData = { ...fileDataRaw, url: resolvedFileUrl, name: resolvedFileName };

        const handleFileDownload = async (url, filename) => {
          try {
            const response = await fetch(url);
            if (!response.ok) throw new Error('Download failed');
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename || 'file';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
          } catch (error) {
            console.error('Download error:', error);
            // Fallback: open in new tab
            window.open(url, '_blank');
            toast.error('Could not download directly. Opening in new tab.');
          }
        };

        return fileData?.url ? (
          <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <File className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium">{fileData.name || 'File'}</p>
                {fileData.description && (
                  <p className="text-sm text-muted-foreground">{fileData.description}</p>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleFileDownload(fileData.url, fileData.name)}
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        ) : null;

      case 'Subtasks':
        const subtasks = item.content || [];
        const subtaskStates = activeTaskRun?.form_data?.[item.id] || {};
        return (
          <div className="space-y-2">
            {subtasks.map((subtask) => (
              <label key={subtask.id} className="flex items-center gap-3 p-2 rounded hover:bg-muted cursor-pointer">
                <Checkbox
                  checked={subtaskStates[subtask.id] || false}
                  onCheckedChange={(checked) => handleSubtaskToggle(item.id, subtask.id, checked)}
                />
                <span className={subtaskStates[subtask.id] ? 'line-through text-muted-foreground' : ''}>
                  {subtask.text}
                </span>
              </label>
            ))}
          </div>
        );

      case 'Page':
        const pageData = item.content;
        if (!pageData) return null;
        return (
          <div className="border rounded-lg p-4 bg-muted/10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{pageData.name}</div>
                <div className="text-xs text-muted-foreground">
                  Referenced page
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/space/${projectId}/page/${pageData.id}`)}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View Page
              </Button>
            </div>
          </div>
        );

      case 'Embed':
        const embedDataRaw = item.content;
        const resolvedEmbedUrl = replaceVariablesInContent(embedDataRaw?.url || '', { ...allFormData, ...flowOutputData }, allFields).replace(/<[^>]+>/g, '');
        if (!resolvedEmbedUrl) return null;
        const isPdfEmbed = /\.pdf(\?|$|#)/i.test(resolvedEmbedUrl);
        return (
          <div className="border rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 border-b text-sm text-muted-foreground">
              <Link2 className="h-4 w-4" />
              <span className="truncate">{resolvedEmbedUrl}</span>
            </div>
            <div className="relative w-full h-[400px] bg-white">
              <iframe
                src={resolvedEmbedUrl}
                className="w-full h-full border-0"
                title="Embedded content"
                {...(!isPdfEmbed && { sandbox: 'allow-same-origin allow-scripts allow-popups allow-forms' })}
                loading="lazy"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading || isLoading) {
    return (
      <AppShell3>
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner />
        </div>
      </AppShell3>
    );
  }

  if (!workflowRun || !workflow) {
    return (
      <AppShell3>
        <div className="flex flex-col items-center justify-center min-h-screen gap-4">
          <p className="text-muted-foreground">Workflow run not found</p>
          <Button onClick={() => navigate(`/space/${projectId}/tasks`)}>
            Back to Tasks
          </Button>
        </div>
      </AppShell3>
    );
  }

  const isCompleted = workflowRun.status === 'completed';
  const isCancelled = workflowRun.status === 'cancelled';
  const isActive = workflowRun.status === 'in_progress' || workflowRun.status === 'pending';

  return (
    <AppShell3 pageTitle="" pageDescription="">
      <PageTransition>
        <ToolHeader
          spaceName={space?.name || 'Space'}
          spaceImage={space?.logo_url}
          toolName={workflowRun.run_name || workflow.name}
          currentTool="tasks"
          spaceTools={spaceTools}
          parentBreadcrumb={{
            name: 'Tasks',
            onClick: () => navigate(`/space/${projectId}/tasks`)
          }}
          actions={
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/space/${projectId}/tasks`)}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Tasks
              </Button>

              {isActive && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelRun}
                  disabled={isCancelling}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  {isCancelling ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Cancelling...
                    </>
                  ) : (
                    <>
                      <Ban className="h-4 w-4 mr-2" />
                      Cancel Run
                    </>
                  )}
                </Button>
              )}

              {isCompleted && (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="text-sm font-medium">Completed</span>
                </div>
              )}

              {isCancelled && (
                <div className="flex items-center gap-2 text-destructive">
                  <XCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">Cancelled</span>
                </div>
              )}
            </div>
          }
        />

        <div className="container mx-auto px-4 py-6">
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">
                {completedCount} of {taskRuns.length} tasks completed
              </span>
              <span className="text-sm font-medium">{Math.round(progressPercent)}%</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </div>

          <div className="flex gap-6">
            {/* Left Sidebar - Task List */}
            <div className="w-80 border rounded-lg bg-card flex flex-col overflow-y-auto sticky top-6 h-[calc(100vh-200px)]">
              {/* Cover Image */}
              {workflow.settings?.notificationCover && (
                <div className="relative">
                  {workflow.settings.notificationCover.type === 'image' ? (
                    <img
                      src={workflow.settings.notificationCover.value}
                      alt="Workflow cover"
                      className="w-full h-32 object-cover rounded-t-lg"
                    />
                  ) : (
                    <div
                      className="w-full h-32 rounded-t-lg"
                      style={{ background: workflow.settings.notificationCover.value }}
                    />
                  )}
                </div>
              )}

              <div className="p-4 flex-1">
                <div className="space-y-1">
                  {visibleTaskRuns.map((taskRun, index) => {
                    const workflowTask = workflow.tasks?.find(t => String(t.id) === taskRun.task_id);
                    const isActiveTask = taskRun.id === activeTaskRunId;
                    const isTaskCompleted = taskRun.status === 'completed';
                    const isTaskCancelled = taskRun.status === 'cancelled';
                    const isFlowNode = workflowTask?.type === 'flow';
                    const isEndNode = workflowTask?.type === 'end';
                    const isSpecialNode = isFlowNode || isEndNode;
                    // Get completed by user - from joined data or look up from teamMembers
                    const completedByUser = taskRun.completed_by_user ||
                      (taskRun.completed_by ? teamMembers.find(m => m.id === taskRun.completed_by) : null);

                    // Determine node-specific colors
                    const nodeColor = isFlowNode ? 'blue' : isEndNode ? 'emerald' : null;

                    return (
                      <button
                        key={taskRun.id}
                        onClick={() => setActiveTaskRunId(taskRun.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                          isActiveTask
                            ? isFlowNode ? 'bg-blue-50 border border-blue-400'
                            : isEndNode ? 'bg-emerald-50 border border-emerald-400'
                            : 'bg-primary/10 border border-primary'
                            : 'hover:bg-muted'
                        }`}
                      >
                        {/* Status Icon */}
                        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                          isTaskCompleted
                            ? isFlowNode ? 'bg-blue-500 text-white' : isEndNode ? 'bg-emerald-500 text-white' : 'bg-green-500 text-white'
                            : isTaskCancelled
                            ? 'bg-destructive/20 text-destructive'
                            : isActiveTask
                            ? isFlowNode ? 'bg-blue-500 text-white' : isEndNode ? 'bg-emerald-500 text-white' : 'bg-primary text-primary-foreground'
                            : isFlowNode ? 'border-2 border-blue-400 text-blue-400' : isEndNode ? 'border-2 border-emerald-400 text-emerald-400' : 'border-2 border-muted-foreground'
                        }`}>
                          {isTaskCompleted ? (
                            isFlowNode ? <Zap className="h-3 w-3" /> : isEndNode ? <FlagTriangleRight className="h-3 w-3" /> : <Check className="h-4 w-4" />
                          ) : isTaskCancelled ? (
                            <X className="h-4 w-4" />
                          ) : isFlowNode ? (
                            <Zap className="h-3 w-3" />
                          ) : isEndNode ? (
                            <FlagTriangleRight className="h-3 w-3" />
                          ) : (
                            <span className="text-xs font-medium">{index + 1}</span>
                          )}
                        </div>

                        {/* Task Name */}
                        <div className="flex-1 min-w-0">
                          <div className={`font-medium truncate ${
                            isTaskCompleted ? 'text-muted-foreground line-through' :
                            isTaskCancelled ? 'text-muted-foreground line-through' : ''
                          }`}>
                            {workflowTask?.name || workflowTask?.formTitle || `Task ${index + 1}`}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {taskRun.due_date && !isTaskCompleted && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                {new Date(taskRun.due_date).toLocaleDateString()}
                              </div>
                            )}
                            {/* Show completed by info for completed tasks */}
                            {isTaskCompleted && completedByUser && (
                              <div className="flex items-center gap-1 text-xs text-green-600">
                                <Avatar className="h-4 w-4">
                                  <AvatarImage src={completedByUser.avatar_url} />
                                  <AvatarFallback className="text-[8px] bg-green-100">
                                    {completedByUser.first_name?.[0]}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="truncate max-w-[100px]">
                                  {completedByUser.first_name}
                                </span>
                              </div>
                            )}
                            {/* Assignee avatars for non-completed tasks */}
                            {!isTaskCompleted && Array.isArray(taskRun.assignees) && taskRun.assignees.length > 0 && (
                              <div className="flex -space-x-1">
                                {taskRun.assignees.slice(0, 3).map((assigneeId) => {
                                  const member = teamMembers.find(m => m.id === assigneeId);
                                  if (!member) return null;
                                  return (
                                    <Avatar key={assigneeId} className="h-4 w-4 border border-background">
                                      <AvatarImage src={member.avatar_url} />
                                      <AvatarFallback className="text-[8px]">
                                        {member.first_name?.[0]}
                                      </AvatarFallback>
                                    </Avatar>
                                  );
                                })}
                                {taskRun.assignees.length > 3 && (
                                  <div className="h-4 w-4 rounded-full bg-muted border border-background flex items-center justify-center text-[8px] font-medium">
                                    +{taskRun.assignees.length - 3}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {isActiveTask && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Main Content - Active Task */}
            <div className="flex-1">
              {/* Flow Execution Loading Screen */}
              {isFlowExecuting ? (
                <div
                  className={`rounded-lg p-6 min-h-[500px] ${
                    !workflow.settings?.background
                      ? 'bg-gradient-to-br from-blue-100 via-blue-200 to-cyan-200'
                      : ''
                  }`}
                  style={
                    workflow.settings?.background
                      ? workflow.settings.background.type === 'image'
                        ? { backgroundImage: `url(${workflow.settings.background.value})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                        : { background: workflow.settings.background.value }
                      : {}
                  }
                >
                  <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl mx-auto p-8 min-h-[400px] flex flex-col items-center justify-center">
                    <FlowExecutionScreen
                      isComplete={false}
                      error={flowError}
                      customMessage={flowExecutionMessage !== 'Running automation...' ? flowExecutionMessage : null}
                      onRetry={() => {
                        setFlowError(null);
                        flowExecutionRef.current = null;
                        setFlowExecutionMessage('Running automation...');
                        setFlowRetryCount(c => c + 1);
                      }}
                      onSkip={() => {
                        setIsFlowExecuting(false);
                        setFlowError(null);
                        flowExecutionRef.current = null;
                        handleCompleteFlowTask();
                      }}
                    />
                  </div>
                </div>
              ) : activeWorkflowTask ? (
                <div
                  className={`rounded-lg p-6 min-h-[500px] ${
                    !workflow.settings?.background
                      ? 'bg-gradient-to-br from-blue-100 via-blue-200 to-cyan-200'
                      : ''
                  }`}
                  style={
                    workflow.settings?.background
                      ? workflow.settings.background.type === 'image'
                        ? { backgroundImage: `url(${workflow.settings.background.value})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                        : { background: workflow.settings.background.value }
                      : {}
                  }
                >
                  <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl mx-auto p-8">
                    {/* End Screen Badge */}
                    {activeWorkflowTask.type === 'end' && (
                      <div className="flex items-center gap-2 mb-3">
                        <div className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium flex items-center gap-1.5">
                          <FlagTriangleRight className="h-3 w-3" />
                          End Screen
                        </div>
                      </div>
                    )}

                    {/* Task Header */}
                    <h2 className="text-2xl font-bold mb-6">
                      {activeWorkflowTask.formTitle || activeWorkflowTask.name || 'Untitled Task'}
                    </h2>

                    <div className={`border-t mb-6 ${activeWorkflowTask.type === 'end' ? 'border-emerald-200' : ''}`} />

                    {/* Task Assignees - not shown for end screens or completed/cancelled tasks */}
                    {!isCompleted && !isCancelled && !isTaskReadOnly && activeWorkflowTask.type !== 'end' && (
                      <div className="mb-6">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                            <Users className="h-4 w-4" />
                            <span>Assignees</span>
                          </div>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" size="sm" className="h-8">
                                <UserPlus className="h-4 w-4 mr-2" />
                                Manage
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72" align="end">
                              <div className="space-y-3">
                                <div className="font-medium text-sm">Assign team members</div>
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1 text-xs"
                                    onClick={handleAssignAllToTask}
                                  >
                                    Assign All
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-1 text-xs"
                                    onClick={handleClearAssignees}
                                  >
                                    Clear All
                                  </Button>
                                </div>
                                <div className="border-t pt-3 max-h-60 overflow-y-auto space-y-1">
                                  {teamMembers.length === 0 ? (
                                    <p className="text-sm text-muted-foreground text-center py-2">No team members found</p>
                                  ) : (
                                    teamMembers.map((member) => {
                                      const isAssigned = Array.isArray(activeTaskRun?.assignees) &&
                                        activeTaskRun.assignees.includes(member.id);
                                      return (
                                        <button
                                          key={member.id}
                                          onClick={() => handleToggleAssignee(member.id)}
                                          className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors"
                                        >
                                          <Checkbox checked={isAssigned} className="pointer-events-none" />
                                          <Avatar className="h-6 w-6">
                                            <AvatarImage src={member.avatar_url} />
                                            <AvatarFallback className="text-xs">
                                              {member.first_name?.[0]}{member.last_name?.[0]}
                                            </AvatarFallback>
                                          </Avatar>
                                          <span className="text-sm truncate">
                                            {member.first_name} {member.last_name}
                                          </span>
                                        </button>
                                      );
                                    })
                                  )}
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>

                        {/* Display current assignees */}
                        {(() => {
                          const assigneeIds = Array.isArray(activeTaskRun?.assignees)
                            ? activeTaskRun.assignees
                            : [];
                          const assignedMembers = teamMembers.filter(m => assigneeIds.includes(m.id));

                          if (assignedMembers.length === 0) {
                            return (
                              <p className="text-sm text-muted-foreground italic">No assignees</p>
                            );
                          }

                          return (
                            <div className="flex flex-wrap gap-2">
                              {assignedMembers.map((member) => (
                                <div
                                  key={member.id}
                                  className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-full"
                                >
                                  <Avatar className="h-5 w-5">
                                    <AvatarImage src={member.avatar_url} />
                                    <AvatarFallback className="text-xs">
                                      {member.first_name?.[0]}{member.last_name?.[0]}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="text-sm">{member.first_name} {member.last_name}</span>
                                  <button
                                    onClick={() => handleToggleAssignee(member.id)}
                                    className="text-muted-foreground hover:text-destructive transition-colors"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {/* Task Content */}
                    <div className="space-y-6">
                      {activeWorkflowTask.content?.filter((item) => {
                        // End screens only show content items
                        if (activeWorkflowTask.type === 'end' && item.category === 'form') return false;
                        // Apply conditional logic filtering
                        const allFormData = getAllFormData();
                        const allFields = getAllFields();
                        return shouldShowField(item, allFormData, allFields);
                      }).map((item) => (
                        <div key={item.id}>
                          {item.category === 'form'
                            ? renderFormField(item)
                            : renderContentItem(item)
                          }
                        </div>
                      ))}
                    </div>

                    {/* Action Buttons */}
                    {activeWorkflowTask.actionButtons?.length > 0 && (
                      <div className="mt-6 space-y-3">
                        {activeWorkflowTask.actionButtons.map((btn) => {
                          // Replace variables in the URL
                          const resolvedUrl = replaceVariablesInContent(btn.url || '', { ...allFormData, ...flowOutputData }, allFields)
                            .replace(/<[^>]+>/g, ''); // Strip any HTML tags from variable replacement

                          const isExternalUrl = resolvedUrl && /^https?:\/\//.test(resolvedUrl);
                          const ButtonIcon = btn.icon === 'download' ? Download
                            : btn.icon === 'external' ? ExternalLink
                            : null;

                          return isExternalUrl ? (
                            <a
                              key={btn.id}
                              href={resolvedUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`inline-flex items-center justify-center w-full h-11 rounded-md text-sm font-medium transition-colors ${
                                btn.style === 'outline'
                                  ? 'border border-input bg-background hover:bg-accent hover:text-accent-foreground'
                                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                              }`}
                            >
                              {ButtonIcon && <ButtonIcon className="h-4 w-4 mr-2" />}
                              {btn.label || 'Action'}
                            </a>
                          ) : (
                            <Button
                              key={btn.id}
                              variant={btn.style === 'outline' ? 'outline' : 'default'}
                              size="lg"
                              className={`w-full ${btn.style !== 'outline' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                              disabled
                            >
                              {ButtonIcon && <ButtonIcon className="h-4 w-4 mr-2" />}
                              {btn.label || 'Action'}
                            </Button>
                          );
                        })}
                      </div>
                    )}

                    {/* Complete Button */}
                    {!isCompleted && !isCancelled && activeTaskRun?.status !== 'completed' && activeTaskRun?.status !== 'cancelled' && (
                      <div className="mt-8 pt-6 border-t">
                        <Button
                          onClick={handleCompleteTask}
                          disabled={isSaving}
                          className={`w-full ${activeWorkflowTask.type === 'end' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-green-600 hover:bg-green-700'}`}
                          size="lg"
                        >
                          {isSaving ? (
                            'Saving...'
                          ) : activeWorkflowTask.type === 'end' ? (
                            <>
                              <FlagTriangleRight className="h-4 w-4 mr-2" />
                              Done
                            </>
                          ) : (
                            <>
                              <Check className="h-4 w-4 mr-2" />
                              Complete Task
                            </>
                          )}
                        </Button>
                      </div>
                    )}

                    {/* Task Already Completed */}
                    {activeTaskRun?.status === 'completed' && (() => {
                      const completedByUser = activeTaskRun.completed_by_user ||
                        (activeTaskRun.completed_by ? teamMembers.find(m => m.id === activeTaskRun.completed_by) : null);
                      return (
                        <div className="mt-8 pt-6 border-t">
                          <div className="p-4 bg-green-50 rounded-lg">
                            <div className="flex items-center justify-center gap-2 text-green-600 mb-3">
                              <CheckCircle2 className="h-5 w-5" />
                              <span className="font-medium">Task Completed</span>
                            </div>
                            {(completedByUser || activeTaskRun.completed_at) && (
                              <div className="flex items-center justify-center gap-3 text-sm text-green-700">
                                {completedByUser && (
                                  <div className="flex items-center gap-2">
                                    <Avatar className="h-6 w-6 border border-green-200">
                                      <AvatarImage src={completedByUser.avatar_url} />
                                      <AvatarFallback className="text-xs bg-green-100 text-green-700">
                                        {completedByUser.first_name?.[0]}{completedByUser.last_name?.[0]}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span>{completedByUser.first_name} {completedByUser.last_name}</span>
                                  </div>
                                )}
                                {completedByUser && activeTaskRun.completed_at && (
                                  <span className="text-green-400">•</span>
                                )}
                                {activeTaskRun.completed_at && (
                                  <span>
                                    {new Date(activeTaskRun.completed_at).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: 'numeric',
                                      minute: '2-digit',
                                    })}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Task Cancelled */}
                    {activeTaskRun?.status === 'cancelled' && (
                      <div className="mt-8 pt-6 border-t">
                        <div className="flex items-center justify-center gap-2 text-destructive p-4 bg-destructive/10 rounded-lg">
                          <XCircle className="h-5 w-5" />
                          <span className="font-medium">Task Cancelled</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[500px] text-muted-foreground">
                  Select a task to view
                </div>
              )}
            </div>
          </div>

          {/* Comments Section - only show if enabled in workflow settings */}
          {workflow?.settings?.runComments && (
            <div className="mt-6">
              <WorkflowRunComments
                workflowRunId={runId}
                workflowName={workflowRun?.run_name || workflow?.name}
                projectId={projectId}
              />
            </div>
          )}
        </div>
      </PageTransition>
    </AppShell3>
  );
}
